import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Box, CircularProgress } from '@mui/material';
import Button from '@/components/CustomButtons/Button';
import { Document, Page } from 'react-pdf';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { RefundsApi } from '@/api/clients/refunds/refunds.api';

export default function ECheckPreviewModal({ open, onClose, refundId }) {
  const { t } = useTranslation();
  const [pdfPreview, setPdfPreview] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (open && refundId) {
      fetchECheckPdf();
    }

    return () => {
      if (pdfPreview) {
        URL.revokeObjectURL(pdfPreview);
      }
      setPdfPreview(null);
      setError(null);
      setPageNumber(1);
      setNumPages(null);
    };
  }, [open, refundId]);

  const fetchECheckPdf = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/download-echeck/${refundId}`,
        { responseType: 'blob' },
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/pdf',
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        setPdfPreview(e.target.result);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Error fetching eCheck PDF:', err);
      setError(t('eCheckPreview.error', 'Failed to load eCheck PDF'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/download-echeck/${refundId}`,
        { responseType: 'blob' },
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `echeck-${refundId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading eCheck PDF:', err);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await RefundsApi.sendECheckEmail(refundId);
    } catch (err) {
      console.error('Error sending eCheck email:', err);
    } finally {
      setSendingEmail(false);
    }
  };

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  const handleClose = () => {
    if (pdfPreview) {
      URL.revokeObjectURL(pdfPreview);
    }
    setPdfPreview(null);
    setError(null);
    setPageNumber(1);
    setNumPages(null);
    onClose();
  };

  return (
    <Modal
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      open={open}
      onClose={handleClose}>
      <Box
        style={{
          height: '90vh',
          background: '#fff',
          borderRadius: '8px',
          width: '90vw',
          maxWidth: '800px',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
        {loading && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <CircularProgress />
          </div>
        )}

        {error && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'red',
            }}>
            {error}
          </div>
        )}

        {!loading && !error && pdfPreview && (
          <>
            <div
              style={{
                flex: 1,
                overflow: 'auto',
                textAlign: 'center',
                border: '1px solid #ccc',
              }}>
              <Document
                file={pdfPreview}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div>{t('eCheckPreview.loading', 'Loading PDF...')}</div>
                }
                error={
                  <div>
                    {t('eCheckPreview.renderError', 'Failed to load PDF')}
                  </div>
                }>
                <Page pageNumber={pageNumber} />
              </Document>
            </div>

            <div style={{ padding: '10px', textAlign: 'center' }}>
              <p>
                {t('eCheckPreview.pageInfo', {
                  current: pageNumber,
                  total: numPages,
                  defaultValue: 'Page {{current}} of {{total}}',
                })}
              </p>

              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'center',
                }}>
                <Button color='primary' onClick={handleDownload}>
                  {t('eCheckPreview.download', 'Download')}
                </Button>
                <Button
                  color='info'
                  onClick={handleSendEmail}
                  disabled={sendingEmail}>
                  {sendingEmail
                    ? t('eCheckPreview.sending', 'Sending...')
                    : t('eCheckPreview.sendEmail', 'Send Email')}
                </Button>
                <Button color='default' onClick={handleClose}>
                  {t('eCheckPreview.close', 'Close')}
                </Button>
              </div>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}
